import React, { useState, useEffect } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────────────────────────────────────
// TASK DEFINITIONS PER EXPERIENCE LEVEL
// ─────────────────────────────────────────────────────────────────────────────

const TASKS = {
  new: [
    { id: 'discover',      roman: 'I',   label: 'Explore Discover',         description: 'Browse fragrances and find ones that appeal to you', link: 'discover', linkLabel: 'Browse Now'        },
    { id: 'wishlist',      roman: 'II',  label: 'Save to your wishlist',    description: 'Find a fragrance you want and save it',              link: 'discover', linkLabel: 'Start Discovering' },
    { id: 'add_fragrance', roman: 'III', label: 'Add your first fragrance', description: 'Once you own a bottle, log it to your library',      link: 'library',  linkLabel: 'Go to Library'     },
  ],
  beginner: [
    { id: 'add_fragrance', roman: 'I',   label: 'Add your first fragrance', description: 'Log a bottle to your library',                                      link: 'library',  linkLabel: 'Go to Library'     },
    { id: 'notes_set',     roman: 'II',  label: 'Set your taste profile',   description: 'Add favourite notes or accords to sharpen your recommendations',   link: 'profile',  linkLabel: 'Edit Preferences'  },
    { id: 'discover',      roman: 'III', label: 'Explore Discover',         description: 'Browse fragrances and find new favourites',                         link: 'discover', linkLabel: 'Browse Now'        },
    { id: 'wishlist',      roman: 'IV',  label: 'Save to your wishlist',    description: 'Add a fragrance you want to try',                                   link: 'discover', linkLabel: 'Start Discovering' },
    { id: 'journal_entry', roman: 'V',   label: 'Log your first wear',      description: 'Tap a fragrance in "Recommended for Today" to log a wear',         link: 'journal',  linkLabel: 'Open Journal'      },
  ],
  intermediate: [
    { id: 'add_fragrance', roman: 'I',   label: 'Add your collection',       description: 'Log all your bottles to your library',                             link: 'library',  linkLabel: 'Go to Library'     },
    { id: 'notes_set',     roman: 'II',  label: 'Set your favourite notes',  description: 'Help Flakon recommend more precisely',                             link: 'profile',  linkLabel: 'Edit Preferences'  },
    { id: 'journal_entry', roman: 'III', label: 'Log your first wear',       description: 'Tap a fragrance in "Recommended for Today" to log a wear',        link: 'journal',  linkLabel: 'Open Journal'      },
    { id: 'discover',      roman: 'IV',  label: 'Explore Discover',          description: 'Find fragrances that fill gaps in your collection',                link: 'discover', linkLabel: 'Browse Now'        },
    { id: 'profile',       roman: 'V',   label: 'Complete your profile',     description: 'Add a bio and favourite house',                                    link: 'profile',  linkLabel: 'Edit Profile'      },
  ],
  expert: [
    { id: 'add_fragrance', roman: 'I',   label: 'Add your full collection',  description: 'Log every bottle so Flakon can track your library',               link: 'library',  linkLabel: 'Go to Library'     },
    { id: 'notes_set',     roman: 'II',  label: 'Set your notes & accords',  description: 'Fine-tune your recommendations',                                  link: 'profile',  linkLabel: 'Edit Preferences'  },
    { id: 'journal_entry', roman: 'III', label: 'Log your first wear',       description: 'Tap a fragrance in "Recommended for Today" to log a wear',        link: 'journal',  linkLabel: 'Open Journal'      },
    { id: 'discover',      roman: 'IV',  label: 'Check your recommendations',description: 'See what Flakon suggests based on your collection',               link: 'discover', linkLabel: 'Browse Now'        },
    { id: 'profile',       roman: 'V',   label: 'Complete your profile',     description: 'Add a bio and make your profile public',                          link: 'profile',  linkLabel: 'Edit Profile'      },
  ],
};

const HEADERS = {
  new:          { eyebrow: 'Welcome',         title: 'Start your journey'      },
  beginner:     { eyebrow: 'Getting Started', title: 'Your first steps'        },
  intermediate: { eyebrow: 'Setting Up',      title: 'Build your library'      },
  expert:       { eyebrow: 'Getting Set Up',  title: 'Import your collection'  },
  default:      { eyebrow: 'Getting Started', title: 'Your first steps'        },
};

// ─────────────────────────────────────────────────────────────────────────────
// PROGRESS FETCHER
// Checks each task against real API data — no guessing
// ─────────────────────────────────────────────────────────────────────────────

const fetchProgress = async (token, taskDefs) => {
  const headers = { Authorization: `Bearer ${token}` };
  const API     = process.env.REACT_APP_API_URL;

  const [libraryRes, journalRes, wishlistRes, profileRes] = await Promise.allSettled([
    axios.get(`${API}/api/library`,  { headers }),
    axios.get(`${API}/api/journal`,  { headers }),
    axios.get(`${API}/api/wishlist`, { headers }),
    axios.get(`${API}/api/profile`,  { headers }),
  ]);

  const c = {};

  // add_fragrance — has at least 1 item in library
  if (libraryRes.status === 'fulfilled') {
    c.add_fragrance = Array.isArray(libraryRes.value.data) && libraryRes.value.data.length > 0;
  }

  // wishlist — has at least 1 item in wishlist
  if (wishlistRes.status === 'fulfilled') {
    c.wishlist = Array.isArray(wishlistRes.value.data) && wishlistRes.value.data.length > 0;
  }

  // journal_entry — has at least 1 completed journal entry
  if (journalRes.status === 'fulfilled') {
    const data    = journalRes.value.data;
    const entries = Array.isArray(data) ? data : (data?.entries ?? []);
    // Only count completed entries, not planned ones
    c.journal_entry = entries.some(e => e.completed === true);
  }

  // profile — has bio, picture, or notes/accords set
  // Also extract userId here so discover key can be scoped per user
  let userId = null;
  if (profileRes.status === 'fulfilled') {
    const user    = profileRes.value.data?.user;
    const profile = profileRes.value.data?.profile;
    userId = user?.id ?? null;
    c.profile   = !!(
      user?.profile_bio ||
      user?.profile_picture_url ||
      profile?.favourite_notes?.length > 0 ||
      profile?.favourite_accords?.length > 0
    );
    // notes_set: only ticks when the user has explicitly set favourite_notes via Profile.
    // Onboarding saves favourite_accords (mood picker) but NOT favourite_notes, so
    // checking notes alone ensures this task can't auto-tick from onboarding alone.
    c.notes_set = !!(profile?.favourite_notes?.length > 0);
  }

  // discover — tracked in localStorage, scoped per user so accounts don't bleed into each other
  const discoverKey = userId ? `visited_discover_${userId}` : 'visited_discover';
  c.discover = localStorage.getItem(discoverKey) === 'true';

  // Only return completion for tasks that exist in this tier's task list
  const taskIds = taskDefs.map(t => t.id);
  const filtered = {};
  taskIds.forEach(id => { filtered[id] = !!c[id]; });

  return { completed: filtered, userId };
};

// ─────────────────────────────────────────────────────────────────────────────
// CHECKLIST CARD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const ChecklistCard = ({ token, onNavigate, isMobile, experience, onDismiss }) => {
  const taskDefs               = TASKS[experience] || TASKS.beginner;
  const { eyebrow }            = HEADERS[experience] || HEADERS.default;
  const [completed, setCompleted] = useState({});
  const [userId, setUserId]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [allDone, setAllDone]     = useState(false);
  const [visible, setVisible]     = useState(true);
  const [expanded, setExpanded]   = useState(false);

  useEffect(() => {
    loadProgress();
  }, [experience]); // re-run if experience changes (library count upgrades tier) // eslint-disable-line

  const loadProgress = async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    try {
      const { completed: result, userId: uid } = await fetchProgress(token, taskDefs);
      setCompleted(result);
      if (uid) setUserId(uid);
      // Only trigger all done if every task in THIS tier is complete
      if (taskDefs.every(t => result[t.id]))
        triggerAllDone();
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const triggerAllDone = () => {
  setAllDone(true);
  setTimeout(() => {
    setVisible(false);
    setTimeout(() => {
      setDismissed(true);
      if (onDismiss) onDismiss();
    }, 400);
  }, 3000);
};

  const handleTaskClick = (task) => {
    if (task.id === 'discover') {
      const discoverKey = userId ? `visited_discover_${userId}` : 'visited_discover';
      localStorage.setItem(discoverKey, 'true');
      const updated = { ...completed, discover: true };
      setCompleted(updated);
      if (taskDefs.every(t => updated[t.id])) triggerAllDone();
    }
    onNavigate(task.link);
  };

  if (dismissed) return null;

  const completedCount = taskDefs.filter(t => completed[t.id]).length;
  const total          = taskDefs.length;
  const progressPct    = (completedCount / total) * 100;
  const remaining      = taskDefs.filter(t => !completed[t.id]);

  return (
    <div style={{
      marginBottom: '32px',
      border: '1px solid #2a2a2a',
      background: '#0e0e0e',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(-8px)',
      transition: 'opacity 0.4s ease, transform 0.4s ease',
      overflow: 'hidden',
    }}>
      {allDone ? (
        // ── Completion state ──
        <div style={{ padding: isMobile ? '24px 20px' : '28px 32px', textAlign: 'center' }}>
          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.45em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '8px' }}>Complete</p>
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 300, color: '#f5f0eb', fontStyle: 'italic' }}>
            You're all set — welcome to Flakon
          </p>
        </div>
      ) : (
        <>
          {/* ── Slim collapsed bar ── */}
          <div
            onClick={() => setExpanded(e => !e)}
            style={{
              padding: isMobile ? '14px 20px' : '14px 24px',
              display: 'flex', alignItems: 'center', gap: '16px',
              cursor: 'pointer', userSelect: 'none',
            }}
          >
            {/* Progress bar */}
            <div style={{ flex: 1, height: '2px', background: '#1a1a1a', position: 'relative', borderRadius: '1px' }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, height: '2px',
                background: '#c9a96e', borderRadius: '1px',
                width: loading ? '0%' : `${progressPct}%`,
                transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1)',
              }} />
            </div>
            {/* Count */}
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.25em', color: '#665e58', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {loading ? '—' : `${completedCount} / ${total}`}
            </p>
            {/* Label */}
            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', flexShrink: 0 }}>
              {eyebrow}
            </p>
            {/* Arrow */}
            <span style={{
              color: '#3a3a3a', fontSize: '0.65rem', flexShrink: 0,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}>▾</span>
          </div>

          {/* Next task hint */}
          {!expanded && !loading && remaining.length > 0 && (
            <div style={{ padding: isMobile ? '0 20px 12px' : '0 24px 12px' }}>
              <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', color: '#3a3a3a' }}>
                Next: <span style={{ color: '#665e58' }}>{remaining[0].label}</span>
              </p>
            </div>
          )}

          {/* ── Expanded task grid ── */}
          {expanded && (
            <>
              <div style={{ height: '1px', background: '#1a1a1a' }} />
              {loading ? (
                <div style={{ padding: isMobile ? '20px' : '24px 32px' }}>
                  <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', color: '#3a3a3a', fontStyle: 'italic' }}>Checking your progress...</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `repeat(${taskDefs.length}, 1fr)`, gap: '1px', background: '#1a1a1a' }}>
                  {taskDefs.map(task => {
                    const done = !!completed[task.id];
                    return (
                      <div key={task.id} style={{
                        background: done ? '#0a0a0a' : '#0e0e0e',
                        padding: isMobile ? '16px 20px' : '20px 20px',
                        display: 'flex', flexDirection: isMobile ? 'row' : 'column',
                        alignItems: isMobile ? 'center' : 'flex-start',
                        gap: isMobile ? '14px' : '0',
                        opacity: done ? 0.45 : 1,
                        transition: 'opacity 0.3s ease, background 0.3s ease',
                      }}>
                        <div style={{ flexShrink: 0, width: isMobile ? '20px' : 'auto', marginBottom: isMobile ? 0 : '12px' }}>
                          {done
                            ? <span style={{ color: '#c9a96e', fontSize: '0.9rem' }}>✓</span>
                            : <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '0.85rem', fontStyle: 'italic', color: '#3a3a3a' }}>{task.roman}</span>
                          }
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.7rem', fontWeight: 400, letterSpacing: '0.04em', color: done ? '#3a3a3a' : '#f5f0eb', marginBottom: '4px', textDecoration: done ? 'line-through' : 'none' }}>{task.label}</p>
                          {!isMobile && <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', fontWeight: 300, color: '#3a3a3a', lineHeight: 1.5 }}>{task.description}</p>}
                        </div>
                        {!done && (
                          <button
                            onClick={() => handleTaskClick(task)}
                            style={{ flexShrink: 0, marginTop: isMobile ? 0 : '14px', background: 'none', border: '1px solid #2a2a2a', color: '#665e58', fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: isMobile ? '6px 12px' : '7px 12px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'border-color 0.2s, color 0.2s', alignSelf: isMobile ? 'center' : 'flex-start' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a96e'; e.currentTarget.style.color = '#c9a96e'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#665e58'; }}
                          >{task.linkLabel}</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Collapse button */}
              {remaining.length > 0 && (
                <div style={{ padding: isMobile ? '12px 20px' : '12px 24px', borderTop: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', color: '#3a3a3a' }}>
                    Next: <span style={{ color: '#665e58' }}>{remaining[0].label}</span>
                  </p>
                  <button
                    onClick={() => setExpanded(false)}
                    style={{ background: 'none', border: 'none', color: '#3a3a3a', fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer' }}
                    onMouseEnter={e => e.target.style.color = '#665e58'}
                    onMouseLeave={e => e.target.style.color = '#3a3a3a'}
                  >Collapse ▴</button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ChecklistCard;